/**
 * Phase 14.5 Step 1 — wizard step-shell UI.
 *
 * Pure presentational component. Renders a horizontal stepper with
 * three steps (Scenario · Dimensions · Review) and a content slot
 * for the active step's body. Step 2 (next iteration of this phase)
 * fills in real form sections. Step 1 ships placeholders.
 *
 * Step navigation is owned by the host page via `currentStep` and
 * `onStepChange` callbacks; this component is stateless so the host
 * can drive `?step=` URL state directly.
 */
import { Check, ChevronLeft, ChevronRight, HelpCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

export const WIZARD_STEPS = [
    { id: 1, label: "Scenario", description: "Pick a scenario + name your campaign." },
    { id: 2, label: "Dimensions", description: "Toggle the variation dimensions to include." },
    { id: 3, label: "Review", description: "Confirm the dispatch plan and submit." },
];

export default function WizardShell({
    currentStep,
    onStepChange,
    onSubmit,
    submitDisabled = true,
    submitLabel = "Submit (placeholder — Step 4)",
    nextDisabled = false,        // Phase 14.8 W4 — gate Step→Step advancement
    nextDisabledReason = null,   //                  on current-step zod validity
    children,
}) {
    const stepCount = WIZARD_STEPS.length;
    const safeStep = Math.min(Math.max(1, currentStep || 1), stepCount);

    const goPrev = () => onStepChange(Math.max(1, safeStep - 1));
    const goNext = () => onStepChange(Math.min(stepCount, safeStep + 1));

    return (
        <div className="space-y-6" data-testid="wizard-shell">
            {/* Phase 14.12 — Help affordance. Opens the Campaigns User
                Guide in a new tab. Operator-facing single-source
                reference for every option in the builder. */}
            <div className="flex items-center justify-between gap-3">
                <div className="text-sm text-ux-text-3">
                    Configure a Monte Carlo or geometry-sweep campaign
                    against a chosen scenario.
                </div>
                <Link
                    to="/help/campaigns"
                    target="_blank"
                    rel="noopener"
                    data-testid="campaign-builder-help-link"
                    className="inline-flex items-center gap-1.5 text-xs text-ux-text-2 hover:text-ux-text-1 underline-offset-4 hover:underline"
                >
                    <HelpCircle className="size-4" />
                    Help &amp; user guide
                </Link>
            </div>
            <ol
                className="flex items-stretch gap-2"
                data-testid="wizard-stepper"
                aria-label="Campaign wizard steps"
            >
                {WIZARD_STEPS.map((s, idx) => {
                    const status =
                        s.id < safeStep ? "complete"
                        : s.id === safeStep ? "current"
                        : "upcoming";
                    return (
                        <li
                            key={s.id}
                            className="flex-1"
                            data-testid={`wizard-step-${s.id}`}
                            data-status={status}
                            aria-current={status === "current" ? "step" : undefined}
                        >
                            <button
                                type="button"
                                onClick={() => onStepChange(s.id)}
                                className={[
                                    "w-full text-left rounded-md border px-4 py-3 transition-colors",
                                    status === "current"
                                        ? "border-ux-text-1 bg-ux-surface-2"
                                        : "border-ux-border-1 bg-ux-surface-1 hover:bg-ux-surface-2/60",
                                ].join(" ")}
                                data-testid={`wizard-step-button-${s.id}`}
                            >
                                <div className="flex items-center gap-2">
                                    <span
                                        className={[
                                            "inline-flex size-6 items-center justify-center rounded-full text-xs font-medium",
                                            status === "complete"
                                                ? "bg-emerald-500/20 text-emerald-300"
                                                : status === "current"
                                                ? "bg-ux-text-1 text-ux-bg-1"
                                                : "bg-ux-surface-2 text-ux-text-3",
                                        ].join(" ")}
                                    >
                                        {status === "complete" ? <Check className="size-3" /> : idx + 1}
                                    </span>
                                    <span className="text-sm font-medium">{s.label}</span>
                                </div>
                                <p className="mt-1 text-xs text-ux-text-3">{s.description}</p>
                            </button>
                        </li>
                    );
                })}
            </ol>

            <div
                className="rounded-md border border-ux-border-1 bg-ux-surface-1 p-6"
                data-testid={`wizard-step-body-${safeStep}`}
            >
                {children}
            </div>

            <div className="flex items-center justify-between">
                <Button
                    type="button"
                    variant="outline"
                    onClick={goPrev}
                    disabled={safeStep <= 1}
                    data-testid="wizard-back-button"
                >
                    <ChevronLeft className="size-4" />
                    Back
                </Button>
                {safeStep < stepCount ? (
                    <Button
                        type="button"
                        onClick={goNext}
                        disabled={nextDisabled}
                        title={nextDisabled && nextDisabledReason ? nextDisabledReason : undefined}
                        data-testid="wizard-next-button"
                    >
                        Next
                        <ChevronRight className="size-4" />
                    </Button>
                ) : (
                    <Button
                        type="button"
                        onClick={onSubmit}
                        disabled={submitDisabled}
                        data-testid="wizard-submit-button"
                    >
                        {submitLabel}
                    </Button>
                )}
            </div>
        </div>
    );
}
